import React, { useMemo } from "react";
import { Formik, FormikErrors } from "formik";
import * as yup from "yup";
import Modal from "../../components/Modal";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { colorPaletteBlue600, textNeutralTertiary } from "../../../core/theme";
import {
	CCOFranceBLMCurtailmentScenario,
	CCOFranceECOCurtailmentType,
	CCOFrancePricingIndex,
	CCOFrancePricingMethod,
	FULL_FLEX_EC_TYPES,
} from "./constants";
import {
	Box,
	Divider,
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Switch,
	TextField,
} from "@mui/material";
import { If } from "../../components/If";
import { Spacer } from "../../components/Spacer";
import { GenerateCurtailmentObjectsModalActions } from "../GenerateCurtailmentObjectsModalActions";

export interface CCOFranceCommonSubFormValues {
	redispatch_compensate_spread: boolean;
	enable_balancing_mechanism: boolean;
	blm_curtailment_scenario: CCOFranceBLMCurtailmentScenario;
	blm_pricing_method: CCOFrancePricingMethod;
	blm_bid_price?: number;
	blm_engie_share?: number;
	blm_compensate_spread: boolean;
	ec_curtailment_type: CCOFranceECOCurtailmentType;
	ec_pricing_method: CCOFrancePricingMethod;
	ec_invoice_price?: number;
	ec_index: string;
	ec_compensate_goo: boolean;
	ec_compensate_spread: boolean;
	enable_full_flex: boolean;
	ff_invoice_price?: number;
}

export const CCOFranceCommonSubFormInitialValues: CCOFranceCommonSubFormValues =
	{
		redispatch_compensate_spread: false,
		enable_balancing_mechanism: false,
		blm_curtailment_scenario: CCOFranceBLMCurtailmentScenario.Fully,
		blm_pricing_method: CCOFrancePricingMethod.InvoicePrice,
		blm_bid_price: undefined,
		blm_engie_share: undefined,
		blm_compensate_spread: false,
		ec_curtailment_type:
			CCOFranceECOCurtailmentType.NoEconomicalCurtailment,
		ec_pricing_method: CCOFrancePricingMethod.InvoicePrice,
		ec_invoice_price: undefined,
		ec_index: "",
		ec_compensate_goo: false,
		ec_compensate_spread: false,
		enable_full_flex: false,
		ff_invoice_price: undefined,
	};

export interface CCOFranceCPSubFormProps {
	onSubmit: (values: CCOFranceCommonSubFormValues) => void;
	isOpen: boolean;
	onClose: () => void;
	existingValues?: CCOFranceCommonSubFormValues;
	onSkip?: () => void;
}

export type CCOFranceCPSubFormValues = CCOFranceCommonSubFormValues;

const style = {
	container: {
		width: "580px",
		paddingLeft: "24px",
		paddingRight: "24px",
		paddingBottom: "24px",
	},
	titleIcon: {
		color: colorPaletteBlue600,
	},
	description: {
		color: textNeutralTertiary,
		fontSize: 20,
		fontWeight: 400,
		fontFamily: "Lato",
	},
	inputsContainer: {
		gap: "40px",
		display: "flex",
	},
	horizontal: {
		display: "flex",
		flexDirection: "row",
	},
	horizontalSpaced: {
		display: "flex",
		flexDirection: "row",
		gap: "40px",
	},
	divider: {
		marginTop: "40px",
		marginBottom: "40px",
	},
	vertical: {
		display: "flex",
		flexDirection: "column",
		gap: "8px",
	},
};

const requiredNumber = (fieldName: string) =>
	yup
		.number()
		.transform((value, originalValue) =>
			originalValue === "" ? undefined : value
		)
		.typeError(`${fieldName} must be a number`)
		.required(`${fieldName} is required`);

const optionalNumber = (fieldName: string) =>
	yup
		.number()
		.transform((value, originalValue) =>
			originalValue === "" ? undefined : value
		)
		.typeError(`${fieldName} must be a number`)
		.notRequired();

export const ccoFranceCPSubFormValidationSchema = yup.object().shape({
	redispatch_compensate_spread: yup.boolean().required(),
	enable_balancing_mechanism: yup.boolean().required(),
	blm_curtailment_scenario: yup
		.string()
		.oneOf(Object.values(CCOFranceBLMCurtailmentScenario))
		.when("enable_balancing_mechanism", {
			is: true,
			then: (schema) =>
				schema.required("Curtailment scenario is required"),
			otherwise: (schema) => schema.notRequired(),
		}),
	blm_pricing_method: yup
		.string()
		.oneOf(Object.values(CCOFrancePricingMethod))
		.when("enable_balancing_mechanism", {
			is: true,
			then: (schema) => schema.required("Pricing method is required"),
			otherwise: (schema) => schema.notRequired(),
		}),
	blm_bid_price: yup.number().when("enable_balancing_mechanism", {
		is: true,
		then: () => requiredNumber("Bid price"),
		otherwise: (schema) => schema.notRequired(),
	}),
	blm_engie_share: optionalNumber("Engie share"),
	blm_compensate_spread: yup.boolean().required(),
	ec_curtailment_type: yup
		.string()
		.oneOf(Object.values(CCOFranceECOCurtailmentType))
		.required("Curtailment type is required"),
	ec_pricing_method: yup
		.string()
		.oneOf(Object.values(CCOFrancePricingMethod))
		.when("ec_curtailment_type", {
			is: (value: CCOFranceECOCurtailmentType) => {
				console.log({ value });
				return (
					value ===
					CCOFranceECOCurtailmentType.NoEconomicalCurtailment
				);
			},
			then: (schema) => schema.notRequired(),
			otherwise: (schema) =>
				schema.required("Pricing method is required"),
		}),
	ec_invoice_price: yup.number().when("ec_curtailment_type", {
		is: (value: CCOFranceECOCurtailmentType) => {
			console.log({ value });
			return (
				value === CCOFranceECOCurtailmentType.NoEconomicalCurtailment
			);
		},
		then: (schema) => schema.notRequired(),
		otherwise: (schema) =>
			schema.when("ec_pricing_method", {
				is: CCOFrancePricingMethod.InvoicePrice,
				then: () => requiredNumber("Invoice price"),
				otherwise: (schema) => schema.notRequired(),
			}),
	}),
	ec_index: yup.string().when("ec_curtailment_type", {
		is: (value: CCOFranceECOCurtailmentType) => {
			console.log({ value });
			return (
				value === CCOFranceECOCurtailmentType.NoEconomicalCurtailment
			);
		},
		then: (schema) => schema.notRequired(),
		otherwise: (schema) =>
			schema.when("ec_pricing_method", {
				is: CCOFrancePricingMethod.Index,
				then: (schema) => schema.required("Index is required"),
				otherwise: (schema) => schema.notRequired(),
			}),
	}),
	ec_compensate_goo: yup.boolean().when("ec_curtailment_type", {
		is: (value: CCOFranceECOCurtailmentType) => {
			console.log({ value });
			return (
				value === CCOFranceECOCurtailmentType.NoEconomicalCurtailment
			);
		},
		then: (schema) => schema.notRequired(),
		otherwise: (schema) => schema.required(),
	}),
	ec_compensate_spread: yup.boolean().when("ec_curtailment_type", {
		is: (value: CCOFranceECOCurtailmentType) => {
			console.log({ value });
			return (
				value === CCOFranceECOCurtailmentType.NoEconomicalCurtailment
			);
		},
		then: (schema) => schema.notRequired(),
		otherwise: (schema) => schema.required(),
	}),
	enable_full_flex: yup.boolean().required(),
	ff_invoice_price: yup.number().when("enable_full_flex", {
		is: true,
		then: () => requiredNumber("Invoice price"),
		otherwise: (schema) => schema.notRequired(),
	}),
});

const getFieldErrorMessage = (
	fieldName: keyof CCOFranceCPSubFormValues,
	errors: FormikErrors<CCOFranceCPSubFormValues>
) => (errors[fieldName] ? String(errors[fieldName]) : undefined);

export function CCOFranceCPSubForm(props: Readonly<CCOFranceCPSubFormProps>) {
	const existingValues = useMemo(
		() => props.existingValues || CCOFranceCommonSubFormInitialValues,
		[props.existingValues]
	);

	return (
		<Formik
			enableReinitialize
			initialValues={existingValues}
			validationSchema={ccoFranceCPSubFormValidationSchema}
			onSubmit={(values) => props.onSubmit(values)}
		>
			{({
				errors,
				handleChange,
				submitForm,
				resetForm,
				touched,
				values,
				setFieldValue,
			}) => (
				<Modal
					open={props.isOpen}
					onClose={props.onClose}
					title={"Generate Curtailment Objects"}
					sx={style.container}
					icon={
						<span style={style.titleIcon}>
							<AddCircleOutlineOutlinedIcon />
						</span>
					}
					actions={
						<GenerateCurtailmentObjectsModalActions
							onClose={props.onClose}
							onSkip={props.onSkip}
							submitForm={submitForm}
							resetForm={resetForm}
						/>
					}
				>
					<Box sx={style.description}>Redispatch</Box>
					<FormControlLabel
						control={
							<Switch
								checked={values.redispatch_compensate_spread}
								onChange={(_, checked) =>
									setFieldValue(
										"redispatch_compensate_spread",
										checked
									)
								}
								name="redispatch_compensate_spread"
								color="primary"
							/>
						}
						label="Compensate Spread"
					/>
					<Divider sx={style.divider} />
					<Box sx={style.description}>Balancing Mechanism (MA)</Box>
					<FormControlLabel
						control={
							<Switch
								checked={values.enable_balancing_mechanism}
								onChange={(_, checked) =>
									setFieldValue(
										"enable_balancing_mechanism",
										checked
									)
								}
								name="enable_balancing_mechanism"
								color="primary"
							/>
						}
						label="On"
					/>
					<If condition={values.enable_balancing_mechanism}>
						<Spacer gap={32} />
						<FormControl
							error={Boolean(
								getFieldErrorMessage(
									"blm_curtailment_scenario",
									errors
								)
							)}
						>
							<FormLabel>Curtailment scenario</FormLabel>
							<RadioGroup
								value={values.blm_curtailment_scenario}
								onChange={(_, value) =>
									setFieldValue(
										"blm_curtailment_scenario",
										value
									)
								}
							>
								<Box sx={style.horizontal}>
									<FormControlLabel
										value={
											CCOFranceBLMCurtailmentScenario.Fully
										}
										control={<Radio />}
										label="Fully Curtailable"
									/>
									<FormControlLabel
										value={
											CCOFranceBLMCurtailmentScenario.CallProgram
										}
										control={<Radio />}
										label="Programme d'appel only"
									/>
								</Box>
							</RadioGroup>
							<FormHelperText>
								{getFieldErrorMessage(
									"blm_curtailment_scenario",
									errors
								)}
							</FormHelperText>
						</FormControl>
						<Spacer gap={32} />
						<FormControl
							error={Boolean(
								getFieldErrorMessage(
									"blm_pricing_method",
									errors
								)
							)}
						>
							<FormLabel>Pricing Method</FormLabel>
							<RadioGroup
								value={values.blm_pricing_method}
								onChange={(_, value) =>
									setFieldValue("blm_pricing_method", value)
								}
							>
								<Box sx={style.horizontal}>
									<FormControlLabel
										value={
											CCOFrancePricingMethod.InvoicePrice
										}
										control={<Radio />}
										label="Invoice Price"
									/>
									<FormControlLabel
										value={
											CCOFrancePricingMethod.ContractPrice
										}
										control={<Radio />}
										label="Contract Price"
									/>
								</Box>
							</RadioGroup>
							<FormHelperText>
								{getFieldErrorMessage(
									"blm_pricing_method",
									errors
								)}
							</FormHelperText>
						</FormControl>
						<Spacer gap={32} />
						<Box sx={style.horizontalSpaced}>
							<FormControl variant="standard">
								<TextField
									label="Bid price"
									value={values.blm_bid_price ?? ""}
									onChange={(ev) =>
										setFieldValue(
											"blm_bid_price",
											ev.target.value
										)
									}
									variant="outlined"
									error={Boolean(
										getFieldErrorMessage(
											"blm_bid_price",
											errors
										)
									)}
								/>
								<FormHelperText
									error={Boolean(
										getFieldErrorMessage(
											"blm_bid_price",
											errors
										)
									)}
								>
									{getFieldErrorMessage(
										"blm_bid_price",
										errors
									) ?? "€/MWh"}
								</FormHelperText>
							</FormControl>
							<FormControl variant="standard">
								<TextField
									label="Engie share (Optional)"
									value={values.blm_engie_share ?? ""}
									onChange={(ev) =>
										setFieldValue(
											"blm_engie_share",
											ev.target.value
										)
									}
									variant="outlined"
									error={Boolean(
										getFieldErrorMessage(
											"blm_engie_share",
											errors
										)
									)}
								/>
								<FormHelperText
									error={Boolean(
										getFieldErrorMessage(
											"blm_engie_share",
											errors
										)
									)}
								>
									{getFieldErrorMessage(
										"blm_engie_share",
										errors
									) ?? "€/MWh"}
								</FormHelperText>
							</FormControl>
						</Box>
						<FormControlLabel
							control={
								<Switch
									checked={values.blm_compensate_spread}
									onChange={(_, checked) =>
										setFieldValue(
											"blm_compensate_spread",
											checked
										)
									}
									name="blm_compensate_spread"
									color="primary"
									data-testid="blm_compensate_spread"
								/>
							}
							label="Compensate Spread"
						/>
					</If>
					<Divider sx={style.divider} />
					<Box sx={style.description}>Economical Curtailment</Box>
					<Spacer gap={32} />
					<FormControl
						fullWidth
						error={Boolean(
							getFieldErrorMessage("ec_curtailment_type", errors)
						)}
					>
						<InputLabel>Curtailment type</InputLabel>
						<Select
							value={values.ec_curtailment_type}
							label="Curtailment type"
							onChange={(ev) => {
								setFieldValue(
									"ec_curtailment_type",
									ev.target.value
								);
							}}
							data-testid="ec_curtailment_type"
						>
							<MenuItem
								value={
									CCOFranceECOCurtailmentType.NoEconomicalCurtailment
								}
								data-testid="ec_curtailment_type_no"
							>
								No Economical Curtailment
							</MenuItem>
							<MenuItem
								value={CCOFranceECOCurtailmentType.DAOnly}
								data-testid="ec_curtailment_type_daonly"
							>
								DA Only
							</MenuItem>
							<MenuItem
								value={CCOFranceECOCurtailmentType.FullFlex}
								data-testid="ec_curtailment_type_fullflex"
							>
								Full Flex
							</MenuItem>
							<MenuItem
								value={CCOFranceECOCurtailmentType.M0DA}
								data-testid="ec_curtailment_type_m0da"
							>
								M0 DA
							</MenuItem>
							<MenuItem
								value={CCOFranceECOCurtailmentType.M0FullFlex}
								data-testid="ec_curtailment_type_m0fullflex"
							>
								M0 Full Flex
							</MenuItem>
							<MenuItem
								value={CCOFranceECOCurtailmentType.BaseprodDA}
								data-testid="ec_curtailment_type_baseprodda"
							>
								Baseprod DA
							</MenuItem>
							<MenuItem
								value={
									CCOFranceECOCurtailmentType.BaseprodFullFlex
								}
								data-testid="ec_curtailment_type_baseprodfullflex"
							>
								Baseprod Full Flex
							</MenuItem>
						</Select>
						<FormHelperText>
							{getFieldErrorMessage(
								"ec_curtailment_type",
								errors
							)}
						</FormHelperText>
					</FormControl>
					<Spacer gap={32} />
					<If
						condition={
							values.ec_curtailment_type !==
							CCOFranceECOCurtailmentType.NoEconomicalCurtailment
						}
					>
						<FormControl
							error={Boolean(
								getFieldErrorMessage(
									"ec_pricing_method",
									errors
								)
							)}
						>
							<FormLabel>Pricing Method</FormLabel>
							<RadioGroup
								value={values.ec_pricing_method}
								onChange={(_, value) =>
									setFieldValue("ec_pricing_method", value)
								}
							>
								<Box sx={style.horizontal}>
									<FormControlLabel
										value={
											CCOFrancePricingMethod.InvoicePrice
										}
										control={<Radio />}
										label="Invoice Price"
									/>
									<FormControlLabel
										value={
											CCOFrancePricingMethod.ContractPrice
										}
										control={<Radio />}
										label="Contract Price"
									/>
									<FormControlLabel
										value={CCOFrancePricingMethod.Index}
										control={<Radio />}
										label="Index Price"
									/>
								</Box>
							</RadioGroup>
							<FormHelperText>
								{getFieldErrorMessage(
									"ec_pricing_method",
									errors
								)}
							</FormHelperText>
						</FormControl>
						<If
							condition={
								values.ec_pricing_method ===
								CCOFrancePricingMethod.InvoicePrice
							}
						>
							<Spacer gap={32} />
							<FormControl variant="standard">
								<TextField
									label="Invoice price"
									value={values.ec_invoice_price ?? ""}
									onChange={(ev) =>
										setFieldValue(
											"ec_invoice_price",
											ev.target.value
										)
									}
									variant="outlined"
									error={Boolean(
										getFieldErrorMessage(
											"ec_invoice_price",
											errors
										)
									)}
								/>
								<FormHelperText
									error={Boolean(
										getFieldErrorMessage(
											"ec_invoice_price",
											errors
										)
									)}
								>
									{getFieldErrorMessage(
										"ec_invoice_price",
										errors
									) ?? "€/MWh"}
								</FormHelperText>
							</FormControl>
						</If>
						<If
							condition={
								values.ec_pricing_method ===
								CCOFrancePricingMethod.Index
							}
						>
							<Spacer gap={32} />
							<FormControl
								error={Boolean(
									getFieldErrorMessage("ec_index", errors)
								)}
							>
								<FormLabel>Index</FormLabel>
								<RadioGroup
									value={values.ec_index}
									onChange={(_, value) =>
										setFieldValue("ec_index", value)
									}
								>
									<Box sx={style.horizontal}>
										<FormControlLabel
											value={
												CCOFrancePricingIndex.EpexSpotFRPowerQHDA
											}
											control={<Radio />}
											label="Epex Spot FR Power QH DA"
										/>
										<FormControlLabel
											value={
												CCOFrancePricingIndex.EpexSpotFRPowerHDA
											}
											control={<Radio />}
											label="Epex Spot FR Power H DA"
										/>
									</Box>
								</RadioGroup>
								<FormHelperText>
									{getFieldErrorMessage("ec_index", errors)}
								</FormHelperText>
							</FormControl>
						</If>
						<Spacer gap={32} />
						<Box sx={style.vertical}>
							<FormControlLabel
								control={
									<Switch
										checked={values.ec_compensate_goo}
										onChange={(_, checked) =>
											setFieldValue(
												"ec_compensate_goo",
												checked
											)
										}
										name="ec_compensate_goo"
										color="primary"
									/>
								}
								label="Compensate GOO"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={values.ec_compensate_spread}
										onChange={(_, checked) =>
											setFieldValue(
												"ec_compensate_spread",
												checked
											)
										}
										name="ec_compensate_spread"
										color="primary"
										data-testid="ec_compensate_spread"
									/>
								}
								label="Compensate Spread"
							/>
						</Box>
					</If>
					<If
						condition={FULL_FLEX_EC_TYPES.includes(
							values.ec_curtailment_type
						)}
					>
						<Divider sx={style.divider} />
						<Box sx={style.description}>Full Flex Premium</Box>
						<FormControlLabel
							control={
								<Switch
									checked={values.enable_full_flex}
									onChange={(_, checked) =>
										setFieldValue(
											"enable_full_flex",
											checked
										)
									}
									name="enable_full_flex"
									color="primary"
									data-testid="enable-full-flex-switch"
								/>
							}
							label="On"
						/>
					</If>
					<If condition={values.enable_full_flex}>
						<Spacer gap={32} />
						<FormControl variant="standard">
							<TextField
								label="Invoice price"
								value={values.ff_invoice_price ?? ""}
								onChange={(ev) =>
									setFieldValue(
										"ff_invoice_price",
										ev.target.value
									)
								}
								variant="outlined"
								error={Boolean(
									getFieldErrorMessage(
										"ff_invoice_price",
										errors
									)
								)}
								data-testid="ff-invoice-price"
							/>
							<FormHelperText
								error={Boolean(
									getFieldErrorMessage(
										"ff_invoice_price",
										errors
									)
								)}
							>
								{getFieldErrorMessage(
									"ff_invoice_price",
									errors
								) ?? "€/MWh"}
							</FormHelperText>
						</FormControl>
					</If>
				</Modal>
			)}
		</Formik>
	);
}
